import { useState } from 'react';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';

export default function Recruitment() {
  const [selectedPosition, setSelectedPosition] = useState('');
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    education: '',
    coverLetter: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5pj71kndmco4977bmb0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          position: '',
          experience: '',
          education: '',
          coverLetter: ''
        });
        setTimeout(() => {
          setShowApplicationForm(false);
          setSubmitStatus('idle');
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openApplicationForm = (position: string) => {
    setFormData(prev => ({ ...prev, position }));
    setShowApplicationForm(true);
  };

  const scrollToForm = () => {
    setShowApplicationForm(true);
    setTimeout(() => {
      document.getElementById('application-form')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero 섹션 */}
      <section className="relative pt-24 pb-20 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/b24b2819e95214aee52810bcfae12d3c.jpg')`
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 to-slate-800/60"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto text-white">
            <div className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm text-white text-sm font-medium rounded-full mb-6">
              JOIN OUR TEAM
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              글로벌 교육의 미래를<br />
              함께 만들어갈 강사를 찾습니다
            </h1>
            <p className="text-xl text-white/90 leading-relaxed mb-8">
              퀀텀하이와 함께 한국과 일본 학습자들에게<br />
              최고의 언어 교육 경험을 제공하세요
            </p>
            <button 
              onClick={scrollToForm}
              className="inline-flex items-center px-8 py-4 bg-orange-500 text-white text-lg font-medium rounded-full hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              지금 지원하기
              <i className="ri-arrow-down-line ml-3"></i>
            </button>
          </div>
        </div>
      </section>

      {/* 왜 퀀텀하이인가 */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              왜 퀀텀하이와 함께해야 할까요?
            </h2>
            <p className="text-xl text-gray-600">
              우리가 제공하는 특별한 근무 환경과 혜택들
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-stone-50 p-8 rounded-2xl text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-time-line text-2xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">유연한 근무 시간</h3>
              <p className="text-gray-600">
                개인 일정에 맞춘 유연한 수업 스케줄 조정 가능
              </p>
            </div>

            <div className="bg-stone-50 p-8 rounded-2xl text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-money-dollar-circle-line text-2xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">경쟁력 있는 보수</h3>
              <p className="text-gray-600">
                경력과 실력에 따른 합리적이고 투명한 급여 체계
              </p>
            </div>

            <div className="bg-stone-50 p-8 rounded-2xl text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-team-line text-2xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">전문적 성장</h3>
              <p className="text-gray-600">
                정기적인 교육과 피드백을 통한 지속적인 전문성 향상
              </p>
            </div>

            <div className="bg-stone-50 p-8 rounded-2xl text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-global-line text-2xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">국제적 경험</h3>
              <p className="text-gray-600">
                한국과 일본 학습자들과의 다양한 문화적 교류 경험
              </p>
            </div>

            <div className="bg-stone-50 p-8 rounded-2xl text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-computer-line text-2xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">최신 교육 도구</h3>
              <p className="text-gray-600">
                효과적인 온라인 수업을 위한 첨단 교육 플랫폼 제공
              </p>
            </div>

            <div className="bg-stone-50 p-8 rounded-2xl text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-heart-line text-2xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">가족 같은 문화</h3>
              <p className="text-gray-600">
                서로를 존중하고 지지하는 협력적인 팀 문화
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 모집 분야 */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              현재 모집 중인 포지션
            </h2>
            <p className="text-xl text-gray-600">
              다양한 분야의 전문 강사를 모집하고 있습니다
            </p>
          </div>

          <div className="space-y-6">
            <div className="bg-white p-8 rounded-2xl border border-gray-200 hover:border-orange-300 hover:shadow-lg transition-all">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                <div className="flex-1 mb-6 lg:mb-0">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                      <i className="ri-english-input text-2xl text-blue-600"></i>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">영어 강사</h3>
                      <p className="text-gray-600">비즈니스 영어 및 일반 영어 회화</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center text-gray-600">
                      <i className="ri-check-line text-green-500 mr-2"></i>
                      원어민 또는 원어민 수준
                    </div>
                    <div className="flex items-center text-gray-600">
                      <i className="ri-check-line text-green-500 mr-2"></i>
                      교육 경력 2년 이상
                    </div>
                    <div className="flex items-center text-gray-600">
                      <i className="ri-check-line text-green-500 mr-2"></i>
                      온라인 수업 경험
                    </div>
                    <div className="flex items-center text-gray-600">
                      <i className="ri-check-line text-green-500 mr-2"></i>
                      한국어 기초 소통 가능
                    </div>
                  </div>
                </div>
                <div className="lg:ml-8">
                  <button 
                    onClick={() => openApplicationForm('영어 강사')}
                    className="w-full lg:w-auto px-6 py-3 bg-orange-500 text-white font-medium rounded-full hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    지원하기
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl border border-gray-200 hover:border-orange-300 hover:shadow-lg transition-all">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                <div className="flex-1 mb-6 lg:mb-0">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4">
                      <i className="ri-translate text-2xl text-red-600"></i>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">일본어 강사</h3>
                      <p className="text-gray-600">기초부터 비즈니스 일본어까지</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center text-gray-600">
                      <i className="ri-check-line text-green-500 mr-2"></i>
                      일본어 원어민 또는 JLPT N1
                    </div>
                    <div className="flex items-center text-gray-600">
                      <i className="ri-check-line text-green-500 mr-2"></i>
                      일본어 교육 자격증 보유
                    </div>
                    <div className="flex items-center text-gray-600">
                      <i className="ri-check-line text-green-500 mr-2"></i>
                      한국어 능통
                    </div>
                    <div className="flex items-center text-gray-600">
                      <i className="ri-check-line text-green-500 mr-2"></i>
                      문화 간 이해도 높음
                    </div>
                  </div>
                </div>
                <div className="lg:ml-8">
                  <button 
                    onClick={() => openApplicationForm('일본어 강사')}
                    className="w-full lg:w-auto px-6 py-3 bg-orange-500 text-white font-medium rounded-full hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    지원하기
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl border border-gray-200 hover:border-orange-300 hover:shadow-lg transition-all">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                <div className="flex-1 mb-6 lg:mb-0">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
                      <i className="ri-presentation-line text-2xl text-purple-600"></i>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">콘텐츠 교육 강사</h3>
                      <p className="text-gray-600">교육 콘텐츠 기획 및 제작</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center text-gray-600">
                      <i className="ri-check-line text-green-500 mr-2"></i>
                      영상 제작 경험
                    </div>
                    <div className="flex items-center text-gray-600">
                      <i className="ri-check-line text-green-500 mr-2"></i>
                      교육 콘텐츠 기획 능력
                    </div>
                    <div className="flex items-center text-gray-600">
                      <i className="ri-check-line text-green-500 mr-2"></i>
                      유튜브/SNS 운영 경험
                    </div>
                    <div className="flex items-center text-gray-600">
                      <i className="ri-check-line text-green-500 mr-2"></i>
                      창의적 사고력
                    </div>
                  </div>
                </div>
                <div className="lg:ml-8">
                  <button 
                    onClick={() => openApplicationForm('콘텐츠 교육 강사')}
                    className="w-full lg:w-auto px-6 py-3 bg-orange-500 text-white font-medium rounded-full hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    지원하기
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 지원 절차 */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              지원 절차
            </h2>
            <p className="text-xl text-gray-600">
              간단하고 투명한 채용 프로세스
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center relative">
              <div className="w-16 h-16 bg-orange-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                1
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">서류 제출</h3>
              <p className="text-gray-600">이력서 및 자기소개서 온라인 제출</p>
              <div className="hidden md:block absolute top-8 -right-4 w-8 h-0.5 bg-orange-200"></div>
            </div>

            <div className="text-center relative">
              <div className="w-16 h-16 bg-orange-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                2
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">화상 면접</h3>
              <p className="text-gray-600">온라인 화상 면접 및 모의 수업</p>
              <div className="hidden md:block absolute top-8 -right-4 w-8 h-0.5 bg-orange-200"></div>
            </div>

            <div className="text-center relative">
              <div className="w-16 h-16 bg-orange-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                3
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">최종 면접</h3>
              <p className="text-gray-600">교육 철학 및 비전 공유</p>
              <div className="hidden md:block absolute top-8 -right-4 w-8 h-0.5 bg-orange-200"></div>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-orange-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                4
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">합격 통보</h3>
              <p className="text-gray-600">최종 결과 통보 및 계약 체결</p>
            </div>
          </div>
        </div>
      </section>

      {/* 지원서 작성 폼 */}
      {showApplicationForm && (
        <section id="application-form" className="py-20 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                지원서 작성
              </h2>
              <p className="text-xl text-gray-600">
                아래 양식을 작성하여 지원해주세요
              </p>
            </div>

            <form onSubmit={handleSubmit} data-readdy-form className="bg-stone-50 p-8 rounded-2xl">
              <div className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-900 mb-2">
                    이름 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    placeholder="홍길동"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-900 mb-2">
                    이메일 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    placeholder="example@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-900 mb-2">
                    연락처 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    placeholder="010-0000-0000"
                  />
                </div>

                <div>
                  <label htmlFor="position" className="block text-sm font-medium text-gray-900 mb-2">
                    지원 포지션 <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="position"
                    name="position"
                    value={formData.position}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  >
                    <option value="">선택해주세요</option>
                    <option value="영어 강사">영어 강사</option>
                    <option value="일본어 강사">일본어 강사</option>
                    <option value="콘텐츠 교육 강사">콘텐츠 교육 강사</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="experience" className="block text-sm font-medium text-gray-900 mb-2">
                    경력 사항 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="experience"
                    name="experience"
                    value={formData.experience}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    placeholder="예: 영어 강사 3년, 온라인 교육 2년"
                  />
                </div>

                <div>
                  <label htmlFor="education" className="block text-sm font-medium text-gray-900 mb-2">
                    학력 및 자격증 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="education"
                    name="education"
                    value={formData.education}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    placeholder="예: 영어교육학 학사, TESOL 자격증"
                  />
                </div>

                <div>
                  <label htmlFor="coverLetter" className="block text-sm font-medium text-gray-900 mb-2">
                    자기소개 및 지원동기 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="coverLetter"
                    name="coverLetter"
                    value={formData.coverLetter}
                    onChange={handleInputChange}
                    required
                    maxLength={500}
                    rows={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm resize-none"
                    placeholder="자기소개와 지원동기를 작성해주세요 (최대 500자)"
                  />
                  <p className="text-sm text-gray-500 mt-1">{formData.coverLetter.length}/500자</p>
                </div>

                {submitStatus === 'success' && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
                    지원서가 성공적으로 제출되었습니다!
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                    제출 중 오류가 발생했습니다. 다시 시도해주세요.
                  </div>
                )}

                <div className="flex gap-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 px-8 py-4 bg-orange-500 text-white font-medium rounded-full hover:bg-orange-600 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  >
                    {isSubmitting ? '제출 중...' : '지원서 제출'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowApplicationForm(false)}
                    className="px-8 py-4 border border-gray-300 text-gray-700 font-medium rounded-full hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    취소
                  </button>
                </div>
              </div>
            </form>
          </div>
        </section>
      )}

      {/* CTA 섹션 */}
      <section className="py-20 bg-slate-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            퀀텀하이와 함께 성장하세요
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-3xl mx-auto">
            글로벌 교육의 미래를 만들어갈 열정적인 강사를 기다립니다.<br />
            지금 바로 지원하여 새로운 도전을 시작하세요!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button 
              onClick={scrollToForm}
              className="inline-flex items-center px-8 py-4 bg-orange-500 text-white text-lg font-medium rounded-full hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              지원서 작성하기
              <i className="ri-edit-line ml-3"></i>
            </button>
            <a 
              href="/contact"
              className="inline-flex items-center px-8 py-4 border border-white text-white text-lg font-medium rounded-full hover:bg-white hover:text-slate-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              채용 문의하기
              <i className="ri-question-line ml-3"></i>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}